import React, { useState } from "react";
import { Palette, Check, Volume2, Loader2, Play } from "lucide-react";
import { useTheme, THEMES, VOICES } from "@/context/ThemeContext";
import { useVoice } from "@/hooks/useVoice";

const PREVIEW_LINE = "The System has chosen you. Rise, Player.";

export default function ThemeSwitcher() {
  const { themeId, setThemeId, voiceId, setVoiceId } = useTheme();
  const { speak } = useVoice();
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const chooseVoice = (id) => {
    setVoiceId(id);
    setPreviewing(id);
    speak(PREVIEW_LINE, () => setPreviewing(null), id);
  };

  return (
    <div className="fixed bottom-5 left-5 z-[60]" data-testid="theme-switcher">
      {open && (
        <div className="mb-3 p-4 rounded-2xl bg-[#0A0E17]/95 border border-white/10 backdrop-blur-xl shadow-2xl w-72 max-h-[70vh] overflow-y-auto animate-[rise_0.25s_ease]">
          {/* Theme */}
          <div className="flex items-center gap-2 mb-3">
            <Palette size={14} className="text-slate-300" />
            <p className="text-[0.6rem] tracking-[0.25em] uppercase text-slate-400 font-mono">Theme</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {THEMES.map((t) => (
              <button key={t.id} data-testid={`theme-${t.id}`} onClick={() => setThemeId(t.id)} title={t.name}
                className="group flex flex-col items-center gap-1">
                <span className="w-9 h-9 rounded-full grid place-items-center border-2 transition-transform group-hover:scale-110"
                  style={{ background: t.swatch, borderColor: themeId === t.id ? "#fff" : "transparent", boxShadow: `0 0 14px ${t.swatch}88` }}>
                  {themeId === t.id && <Check size={16} className="text-black" />}
                </span>
                <span className="text-[0.62rem] text-slate-400">{t.name}</span>
              </button>
            ))}
          </div>

          {/* Voice */}
          <div className="flex items-center gap-2 mb-3">
            <Volume2 size={14} className="text-slate-300" />
            <p className="text-[0.6rem] tracking-[0.25em] uppercase text-slate-400 font-mono">ARCHON's Voice</p>
          </div>
          <div className="space-y-1.5">
            {VOICES.map((v) => {
              const active = voiceId === v.id;
              return (
                <button key={v.id} data-testid={`voice-${v.id}`} onClick={() => chooseVoice(v.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-all ${active ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/5 hover:bg-white/5"}`}>
                  <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${active ? "bg-cyan-400 text-black" : "bg-white/5 text-slate-300"}`}>
                    {previewing === v.id ? <Loader2 size={13} className="animate-spin" /> : active ? <Check size={14} /> : <Play size={12} />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-100 font-medium">{v.name}</span>
                    <span className="block text-[0.68rem] text-slate-500 font-mono">{v.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[0.62rem] text-slate-600 mt-3 px-1">Tap a voice to preview & select.</p>
        </div>
      )}
      <button data-testid="theme-toggle-btn" onClick={() => setOpen((o) => !o)}
        className="w-12 h-12 rounded-full grid place-items-center bg-[#0A0E17]/90 border border-white/15 backdrop-blur-xl text-slate-200 hover:text-white hover:border-white/40 transition-all shadow-lg"
        title="Customize">
        <Palette size={20} />
      </button>
    </div>
  );
}
